import { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import backend from '../services/backend';
import { AnimatedNumber } from './AnimatedNumber';
import { formatNumber } from '../lib/numberFormat';
import { useQuery } from '@tanstack/react-query';
import { usePersistedState } from '../hooks/usePersistetState';
import { useDocumentDarkClass } from '../hooks/useDocumentDarkClass';
import PipelineHealthStrip from './PipelineHealthStrip';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Refresh interval in milliseconds (5 seconds)
const REFRESH_INTERVAL = 5000;

// Light palette: blue-400, emerald-400, amber-400, red-400, violet-400, pink-400
const BAR_COLORS_LIGHT = ['#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#F472B6'];

// Dark palette: blue-500/75, emerald-500/75, yellow-400/75, red-400/75, violet-400/75, pink-400/75
const BAR_COLORS_DARK = [
  'rgba(59, 130, 246, 0.75)',   // blue-500
  'rgba(52, 211, 153, 0.75)',   // emerald-400
  'rgba(250, 204, 21, 0.75)',   // yellow-400
  'rgba(248, 113, 113, 0.75)',  // red-400
  'rgba(167, 139, 250, 0.75)',  // violet-400
  'rgba(244, 114, 182, 0.75)',  // pink-400
];

interface DocumentStatsProps {
  className?: string;
}

type ChartView = 'bar' | 'pie';

export default function DocumentStats({ className = '' }: DocumentStatsProps) {
  const isDark = useDocumentDarkClass();
  const [chartView, setChartView] = usePersistedState<ChartView>('categoryChartView', 'bar');

  const { data: stats, isLoading: loading, error: fetchError, dataUpdatedAt } = useQuery({
    queryKey: ['documentStats'],
    queryFn: () => backend.getDocumentStats().then((res) => res.data),
    refetchInterval: REFRESH_INTERVAL,
  });

  const error = fetchError?.message ?? null;
  const lastUpdated = new Date(dataUpdatedAt);

  const categoryData = useMemo(() => {
    if (!stats) {
      return {
        labels: [] as string[],
        datasets: [
          {
            data: [] as number[],
            backgroundColor: [] as string[],
            borderColor: '#ffffff',
            borderWidth: 0,
          },
        ],
      };
    }
    const keys = Object.keys(stats.byCategory);
    const values = Object.values(stats.byCategory);
    const palette = isDark ? BAR_COLORS_DARK : BAR_COLORS_LIGHT;
    const backgroundColor = keys.map((_, i) => palette[i % palette.length]);
    return {
      labels: keys,
      datasets: [
        {
          data: values,
          backgroundColor,
          borderColor: isDark ? 'rgb(39 39 42)' : '#ffffff', // zinc-900 / white
          borderWidth: 1,
        },
      ],
    };
  }, [stats, isDark]);

  if (loading) {
    return (
      <div className={`bg-white dark:bg-zinc-800 shadow rounded-lg p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-zinc-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-zinc-700 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 dark:bg-zinc-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className={`bg-white dark:bg-zinc-800 shadow rounded-lg p-6 ${className}`}>
        <div className="text-center text-red-600 dark:text-red-400">{error || 'Failed to load statistics'}</div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Document Statistics</h2>
        <span className="text-sm text-gray-500 dark:text-zinc-400">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </span>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5 mb-8">
        <div className="bg-blue-50 dark:bg-zinc-700 rounded-lg p-4">
          <dt className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Documents</dt>
          <dd className="mt-1 text-3xl font-semibold text-blue-900 dark:text-blue-100">
            <AnimatedNumber value={stats.total} format={(v) => formatNumber(v)} />
          </dd>
        </div>
        <div className="bg-blue-50 dark:bg-zinc-700 rounded-lg p-4">
          <dt className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Pages</dt>
          <dd className="mt-1 text-3xl font-semibold text-blue-900 dark:text-blue-100">
            <AnimatedNumber value={stats.documentPagesTotal} format={(v) => formatNumber(v)} />
          </dd>
        </div>
        <div className="bg-purple-50 dark:bg-zinc-700 rounded-lg p-4">
          <dt className="text-sm font-medium text-purple-600 dark:text-purple-400">Ready for Training</dt>
          <dd className="mt-1 text-3xl font-semibold text-purple-900 dark:text-purple-100">
            <AnimatedNumber value={stats.byStatus.readyForTraining} format={(v) => formatNumber(v)} />
          </dd>
        </div>
        <div className="bg-yellow-50 dark:bg-zinc-700 rounded-lg p-4">
          <dt className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Processing</dt>
          <dd className="mt-1 text-3xl font-semibold text-yellow-900 dark:text-yellow-100">
            <AnimatedNumber value={stats.byStatus.processing} format={(v) => formatNumber(v)} />
            <span className="text-gray-500 dark:text-zinc-400">/</span>
            <AnimatedNumber
              value={stats.byStatus.pending + stats.byStatus.processing}
              format={(v) => formatNumber(v)}
            />
          </dd>
        </div>
        <div className="bg-red-50 dark:bg-zinc-700 rounded-lg p-4">
          <dt className="text-sm font-medium text-red-600 dark:text-red-400">Failed</dt>
          <dd className="mt-1 text-3xl font-semibold text-red-900 dark:text-red-100">
            <AnimatedNumber value={stats.byStatus.error} format={(v) => formatNumber(v)} />
          </dd>
        </div>
      </div>

      {/* Category Distribution */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Category Distribution</h3>
          <div className="flex rounded-md border border-gray-200 dark:border-zinc-600 overflow-hidden text-xs">
            <button
              onClick={() => setChartView('bar')}
              className={`px-2.5 py-1 transition-colors ${
                chartView === 'bar'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              Bar
            </button>
            <button
              onClick={() => setChartView('pie')}
              className={`px-2.5 py-1 transition-colors ${
                chartView === 'pie'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              Pie
            </button>
          </div>
        </div>
        <div className="h-64">
            {chartView === 'bar' ? (
              <Bar
                data={categoryData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: isDark ? 'rgb(39 39 42)' : 'rgb(255 255 255)', // zinc-900 / white
                      titleColor: isDark ? '#f4f4f5' : '#18181b',                     // zinc-100 / zinc-950
                      bodyColor: isDark ? '#d4d4d8' : '#3f3f46',                      // zinc-300 / zinc-700
                      borderColor: isDark ? 'rgb(63 63 70)' : 'rgb(228 228 231)',     // zinc-700 / zinc-200
                      borderWidth: 1,
                    },
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { display: false },
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: isDark ? 'rgba(63, 63, 70, 0.5)' : 'rgba(228, 228, 231, 0.5)', // zinc-700/50 / zinc-200/50
                      },
                      ticks: {
                        color: isDark ? '#a1a1aa' : '#52525b', // zinc-400 / zinc-600
                      },
                    },
                  },
                }}
              />
            ) : (
              <Pie
                data={categoryData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        pointStyle: 'circle',
                        usePointStyle: true,
                        color: isDark ? '#a1a1aa' : '#52525b', // zinc-400 / zinc-600
                        filter: (item, data) => (data.labels?.indexOf(item.text) ?? 0) < 10,
                      },
                    },
                    tooltip: {
                      backgroundColor: isDark ? 'rgb(39 39 42)' : 'rgb(255 255 255)', // zinc-900 / white
                      titleColor: isDark ? '#f4f4f5' : '#18181b',                     // zinc-100 / zinc-950
                      bodyColor: isDark ? '#d4d4d8' : '#3f3f46',                      // zinc-300 / zinc-700
                      borderColor: isDark ? 'rgb(63 63 70)' : 'rgb(228 228 231)',     // zinc-700 / zinc-200
                      borderWidth: 1,
                    },
                  },
                }}
              />
            )}
        </div>
      </div>

      <PipelineHealthStrip className="mt-8" />
    </div>
  );
}
